import type { FC, ReactNode } from "react";
import { Suspense, useCallback } from "react";
import { useLocation, useRouteLoaderData } from "react-router";

import { useClientContext } from "~/contexts/client-context";
import type { PagePreview, Pages } from "~/core";
import type { PageConstructorData } from "~/core/services/page-constructor.service";
import type { loader as rootLoader } from "~/root";
import { useDirectus } from "~/shared/utils/directus/useDirectus";
import { useAction } from "~/widgets/use-action";

import {
  type PageConstructorRenderContext,
  type RenderedBlock,
  resolveBlock,
  resolveBlockType,
} from "./block-definitions";
import { LazyBlock } from "./LazyBlock";
import type { BlockType } from "./min-heights";
import { MinHeightPlaceholder } from "./MinHeightPlaceholder";

export type PageConstructorProps = {
  data: PageConstructorData;
  pages?: Array<PagePreview>;
};

const getPageUrl = (detailedPage: Pages): string => {
  if (!detailedPage?.slug) {
    return "/";
  }

  const { segment, slug } = detailedPage;

  return segment ? `/${segment}/${slug}` : `/${slug}`;
};

const LCP_BLOCK_TYPES = new Set<BlockType>([
  "mainBanner",
  "mainBannerV3",
  "mainBannerV4",
  "mainSlider",
  "welcomeOfferBanner",
]);

const CHEAP_EAGER_BLOCK_TYPES = new Set<BlockType>([
  "pageBreadcrumbs",
  "spacer",
  "textBlock",
]);

const FALLBACK_EAGER_IMAGE_BLOCK_TYPES = new Set<BlockType>([
  "cardGallery",
  "gridBlock",
  "gridBlockV2",
]);

const MAX_FALLBACK_EAGER_IMAGE_BLOCK_INDEX = 3;

const renderWithLazyGate = ({
  block,
  index,
  isBot,
  shouldRenderEager,
}: {
  block: RenderedBlock;
  index: number;
  isBot: boolean;
  shouldRenderEager: boolean;
}): ReactNode => {
  if (shouldRenderEager && block.eagerBlock) {
    return (
      <Suspense
        key={index}
        fallback={<MinHeightPlaceholder blockType={block.blockType} />}
      >
        {block.eagerBlock}
      </Suspense>
    );
  }

  return (
    <LazyBlock
      key={index}
      blockType={block.blockType}
      isBot={isBot}
      forcedEager={shouldRenderEager && !block.eagerBlock}
    >
      {block.lazyBlock}
    </LazyBlock>
  );
};

export const PageConstructor: FC<PageConstructorProps> = ({ data, pages }) => {
  const { getDirectusImage } = useDirectus();
  const location = useLocation();
  const { breadcrumbsStaticTranslations } = useClientContext();
  const { callAction: onCallAction, isActionInactive } = useAction();
  const isBot = useRouteLoaderData<typeof rootLoader>("root")?.isBot ?? false;

  const isActive = useCallback(
    (pageItem: PagePreview): boolean => {
      const parts = location.pathname.split("/").filter(Boolean);
      const lastSegment = parts[parts.length - 1] || "/";

      return pageItem.slug === lastSegment;
    },
    [location.pathname],
  );

  const context: PageConstructorRenderContext = {
    data,
    pages,
    getDirectusImage,
    getUrl: getPageUrl,
    isActive,
    breadcrumbsHomeLabel: breadcrumbsStaticTranslations?.home_label,
    onCallAction,
    isActionInactive,
  };

  const components = data.page?.translations?.[0]?.data ?? [];
  const firstLcpBlockIndex = components.findIndex((component) => {
    const blockType = resolveBlockType(component?.item);
    return blockType !== null && LCP_BLOCK_TYPES.has(blockType);
  });

  const blocks = components.map((component, index) =>
    resolveBlock(component?.item, index, context, {
      isPriorityBlock: index === firstLcpBlockIndex,
    }),
  );
  const firstPostLcpBlockIndex =
    firstLcpBlockIndex >= 0
      ? blocks.findIndex(
          (block, index) => index > firstLcpBlockIndex && block !== null,
        )
      : -1;

  return blocks.map((block, index) => {
    const shouldRenderEager =
      block !== null &&
      (index === firstLcpBlockIndex ||
        index === firstPostLcpBlockIndex ||
        (index < 2 && CHEAP_EAGER_BLOCK_TYPES.has(block.blockType)) ||
        (firstLcpBlockIndex < 0 &&
          index <= MAX_FALLBACK_EAGER_IMAGE_BLOCK_INDEX &&
          FALLBACK_EAGER_IMAGE_BLOCK_TYPES.has(block.blockType)));

    return block
      ? renderWithLazyGate({ block, index, isBot, shouldRenderEager })
      : null;
  });
};
